import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import {
  Box,
  IconButton,
  Stack,
  Drawer,
  Typography,
  Divider,
  Button,
} from "@mui/material";
import { FormikHelpers } from "formik";
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from "@mui/x-data-grid";
import { ConfirmationDialog, CustomDataGrid } from "@shared/components";
import {
  formatDateTime,
  getActionPermission,
  getCapitalizeString,
} from "@shared/utils/functions";
import { useGetAllPublishers } from "../services/useGetAllPublishers";
import { AllowedSortFields } from "../utils/constants";
import {
  PublisherRow,
  PublisherDataGridProps,
  PublisherFormValues,
  SortField,
} from "../utils/types";
import PublisherForm from "./PublisherForm";
import { useUpdatePublisher } from "../services/useUpdatePublisher";
import { useDeletePublisher } from "../services/useDeletePublisher";
import { useSortModel } from "@shared/components/grid-components/hooks/useSortModel";
import { TableActions } from "@shared/utils/constants";
import { Can } from "@core/casl-provider";

const PublisherDataGrid = ({
  visibleColumns,
  setPaginationModel,
  paginationModel,
}: PublisherDataGridProps) => {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("searchQuery") ?? "";
  const updatedSince = searchParams.get("updatedSince") ?? "";
  const [actionState, setActionState] = useState<{
    action: "EDIT" | "DELETE" | "";
    payload: Partial<PublisherFormValues> & { id?: number; name?: string };
  }>({
    action: "",
    payload: {},
  });

  const { sortModel, handleSortModelChange, orderBy, order } =
    useSortModel<SortField>({
      allowedSortFields: AllowedSortFields,
    });

  const { data: publisherPartiesList, isLoading } = useGetAllPublishers({
    data: {
      pageNumber: String(paginationModel.page + 1),
      pageSize: String(paginationModel.pageSize),
      // Map unified searchQuery to titleName for backend filtering compatibility
      searchQuery: searchQuery,
      orderBy: orderBy,
      order: order,
      updatedSince,
    },
  });

  const { mutate: deleteMutation } = useDeletePublisher();
  const { mutateAsync: updateFormMutationAsync, isPending: isFormPending } =
    useUpdatePublisher();

  const [allConfigurations, setAllConfigurations] = useState<PublisherRow[]>();

  const totalRows: number =
    (publisherPartiesList?.pagination?.total as number) ??
    allConfigurations?.length ??
    0;

  const clearActionState = () => setActionState({ action: "", payload: {} });

  const handleDeleteConfirm = async () => {
    deleteMutation({ id: String(actionState.payload.id) });
    clearActionState();
  };

  const handleEditSubmit = async (
    values: PublisherFormValues & { id?: number },
    helpers: FormikHelpers<PublisherFormValues & { id?: number }>
  ) => {
    try {
      const id = actionState.payload.id;
      const payloadData = { ...values };
      delete payloadData.id;

      await updateFormMutationAsync({
        payload: { id: id as number, payloadData },
      });
      clearActionState();
    } catch (error: unknown) {
      const errMsg: string =
        error instanceof Error
          ? error.message
          : "Failed to update configuration";
      enqueueSnackbar(errMsg, {
        variant: "error",
        autoHideDuration: 5000,
        preventDuplicate: true,
      });
    } finally {
      helpers.setSubmitting(false);
    }
  };

  // Drawer state for JSON preview
  const [jsonDrawer, setJsonDrawer] = useState<{
    open: boolean;
    title?: string;
    jsonText: string;
  }>({
    open: false,
    jsonText: "",
  });

  const openJsonDrawer = (
    title: string | undefined,
    value: PublisherRow["jsonConfig"]
  ) => {
    let pretty = "";
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        pretty = JSON.stringify(parsed, null, 2);
      } catch {
        // raw when not valid JSON
        pretty = value ?? "";
      }
    } else if (value) {
      pretty = JSON.stringify(value, null, 2);
    }
    setJsonDrawer({ open: true, title, jsonText: pretty });
  };

  const columns: GridColDef<PublisherRow>[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 160,
      sortable: true,
      renderCell: (params: GridRenderCellParams<PublisherRow, string>) => (
        <span style={{ textTransform: "capitalize" }}>{params.value}</span>
      ),
    },
    {
      field: "jsonConfig",
      headerName: "Json Config",
      flex: 1.5,
      minWidth: 220,
      sortable: false,
      renderCell: (params: GridRenderCellParams<PublisherRow, string>) => {
        const text =
          typeof params.value === "string"
            ? params.value
            : JSON.stringify(params.value ?? "");
        const trimmed = text.length > 120 ? text.slice(0, 120) + "…" : text;
        return (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ overflow: "hidden" }}
          >
            <span
              style={{
                fontFamily: "monospace",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "inline-block",
                maxWidth: "100%",
              }}
              title={text}
            >
              {trimmed}
            </span>
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openJsonDrawer(params.row.name, params.row.jsonConfig);
              }}
            >
              Open
            </Button>
          </Stack>
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 1,
      minWidth: 210,
      sortable: true,
      valueFormatter: (value) => formatDateTime(value),
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 1,
      minWidth: 210,
      sortable: true,
      valueFormatter: (value) => formatDateTime(value),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      renderCell: (
        params: GridRenderCellParams<
          PublisherRow,
          unknown,
          unknown,
          GridTreeNodeWithRender
        >
      ) => (
        <Stack
          direction="row"
          spacing={1}
          alignItems={"center"}
          sx={{ height: "100%" }}
        >
          {TableActions.map((action, index) => {
            const actionPermission = getActionPermission(action);

            return (
              <Can
                do={actionPermission}
                on="Publisher"
                key={index}
              >
                <IconButton
                  key={index}
                  title={action.label}
                  style={{
                    backgroundColor: "primary.main",
                  }}
                  onClick={() =>
                    setActionState({
                      action: action.key,
                      payload: {
                        id: params.row.id,
                        name: params.row.name,
                        jsonConfig: params.row.jsonConfig,
                      },
                    })
                  }
                >
                  {action.icon}
                </IconButton>
              </Can>
            );
          })}
        </Stack>
      ),
    },
  ];

  useEffect(() => {
    if (publisherPartiesList?.data) {
      const data = publisherPartiesList.data.map((item) => ({
        ...item,
        id: item.id,
      }));
      setAllConfigurations(data as PublisherRow[]);
    }
  }, [publisherPartiesList]);

  return (
    <>
      <Box
        sx={(theme) => ({
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          borderBottomLeftRadius: theme.shape.borderRadius,
          borderBottomRightRadius: theme.shape.borderRadius,
          overflow: "hidden",
        })}
      >
        <CustomDataGrid<PublisherRow>
          rows={allConfigurations}
          columns={columns}
          visibleColumns={visibleColumns}
          getRowId={(row) => row.id}
          rowCount={totalRows}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          autoHeight
          disableSelectionOnClick
          loading={isLoading}
          dataGridSx={(theme) => ({
            "& .MuiDataGrid-columnHeaders": {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          })}
        />
      </Box>

      <ConfirmationDialog
        open={actionState.action === "DELETE"}
        onClose={clearActionState}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${actionState.payload.name ?? "Publisher"}`}
        message="Are you sure you want to Delete?"
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="primary"
      />
      <PublisherForm
        open={actionState.action === "EDIT"}
        onClose={clearActionState}
        title={"Edit Publisher"}
        initialValues={actionState.payload}
        disable={isFormPending}
        onSubmit={handleEditSubmit}
        showCloseButton
      />
      {/* JSON Side Drawer */}
      <Drawer
        anchor="right"
        open={jsonDrawer.open}
        onClose={() => setJsonDrawer((s) => ({ ...s, open: false }))}
        PaperProps={{ sx: { width: { xs: "100%", sm: 520 } } }}
      >
        <Box
          sx={{
            p: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography
              variant="h6"
              component="h2"
            >
              {jsonDrawer.title
                ? `${getCapitalizeString(jsonDrawer.title)} JSON`
                : "JSON"}
            </Typography>
            <Button
              onClick={() => setJsonDrawer((s) => ({ ...s, open: false }))}
            >
              Close
            </Button>
          </Stack>
          <Divider />
          <Box
            sx={{
              mt: 2,
              flex: 1,
              overflow: "auto",
              bgcolor: (t) => t.palette.background.default,
              borderRadius: 1,
              p: 2,
            }}
          >
            <pre
              style={{
                margin: 0,
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                fontSize: 12,
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {jsonDrawer.jsonText || "No JSON available."}
            </pre>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default PublisherDataGrid;
